import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import {
  useGetOrganizationTitles,
  useRemoveTitleFromMember,
  useReorderOrganizationTitles
} from 'api/queries/organizations/titles';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { useCallback, useMemo } from 'react';
import TitlesRow from '../TitlesRow';

interface Props {
  organizationId: string;
}

const TitlesList = ({ organizationId }: Props) => {
  const { t } = useTranslation(['organizations']);
  const { data: titles } = useGetOrganizationTitles({
    organizationId
  });

  const reorderMutation = useReorderOrganizationTitles();
  const removeTitleFromMemberMutation = useRemoveTitleFromMember();

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const items = Array.from(titles?.items || []);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      reorderMutation.mutate({
        organizationId,
        data: {
          titles: items.map((title, index) => ({
            id: title.id,
            hierarchy: index
          }))
        }
      });
    },
    [organizationId, reorderMutation, titles?.items]
  );

  const sortedTitles = useMemo(
    () => titles?.items?.sort((a, b) => a.hierarchy - b.hierarchy),
    [titles?.items]
  );

  const onClickRemoveTitleFromMember = ({
    organizationId,
    userId,
    titleId
  }: {
    organizationId: string;
    userId: string;
    titleId: string;
  }) => {
    removeTitleFromMemberMutation.mutate({
      organizationId,
      userId,
      titleId
    });
  };

  return (
    <div className="mt-10">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="titles">
          {(provided) => (
            <div ref={provided.innerRef} className="space-y-4">
              {sortedTitles?.map((title, index) => (
                <TitlesRow
                  key={title.id}
                  index={index}
                  title={title}
                  organizationId={organizationId}
                  onClickRemoveTitleFromMember={onClickRemoveTitleFromMember}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Link href={`/organizations/${organizationId}/members/titles/create`}>
        <div className="mt-10 text-primary-700 text-center">
          {t('titleManagement.createNewTitle')}
        </div>
      </Link>
    </div>
  );
};

export default TitlesList;
